import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ClientFilters = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  serviceFilter,
  onServiceFilterChange,
  sortBy,
  onSortChange,
  onClearFilters
}) => {
  const { t } = useTranslation();

  const statusOptions = [
    { value: 'all', label: t('clients.filter.allStatuses') },
    { value: 'active', label: t('clients.statusActive') },
    { value: 'inactive', label: t('clients.statusInactive') },
    { value: 'vip', label: t('clients.statusVip') }
  ];

  const serviceOptions = [
    { value: 'all', label: t('clients.filter.allServices') },
    { value: 'tuns-barbati', label: 'Tuns Barbati' },
    { value: 'tuns-femei', label: 'Tuns Femei' },
    { value: 'vopsit', label: 'Vopsit' },
    { value: 'styling', label: 'Styling' },
    { value: 'tratamente', label: 'Tratamente' },
    { value: 'manichiura', label: 'Manichiura' },
    { value: 'pedichiura', label: 'Pedichiura' }
  ];

  const sortOptions = [
    { value: 'name-asc', label: t('clients.sort.nameAsc') },
    { value: 'name-desc', label: t('clients.sort.nameDesc') },
    { value: 'last-visit-desc', label: t('clients.sort.lastVisitDesc') },
    { value: 'last-visit-asc', label: t('clients.sort.lastVisitAsc') },
    { value: 'total-spent-desc', label: t('clients.sort.totalSpentDesc') },
    { value: 'total-spent-asc', label: t('clients.sort.totalSpentAsc') },
    { value: 'appointments-desc', label: t('clients.sort.appointmentsDesc') },
    { value: 'appointments-asc', label: t('clients.sort.appointmentsAsc') }
  ];

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || serviceFilter !== 'all' || sortBy !== 'name-asc';

  return (
    <div className="bg-background border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-card-foreground">{t('clients.filterTitle')}</h3>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            iconName="X"
            iconPosition="left"
            className="text-muted-foreground hover:text-foreground"
          >
            {t('clients.clearFilters')}
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Search Input */}
        <div className="lg:col-span-2">
          <Input
            type="search"
            placeholder={t('clients.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => onSearchChange(e?.target?.value)}
            className="w-full"
          />
        </div>

        {/* Status Filter */}
        <Select
          placeholder={t('clients.filter.allStatuses')}
          options={statusOptions}
          value={statusFilter}
          onChange={onStatusFilterChange}
        />

        {/* Service Filter */}
        <Select
          placeholder={t('clients.filter.allServices')}
          options={serviceOptions}
          value={serviceFilter}
          onChange={onServiceFilterChange}
        />
      </div>
      {/* Sort Options */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <Icon name="ArrowUpDown" size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{t('clients.sortBy')}</span>
        </div>

        <Select
          options={sortOptions}
          value={sortBy}
          onChange={onSortChange}
          className="w-48"
        />
      </div>
      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border">
          <span className="text-sm text-muted-foreground">{t('clients.activeFilters')}</span>

          {searchTerm && (
            <div className="flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
              <Icon name="Search" size={12} />
              <span>"{searchTerm}"</span>
              <button onClick={() => onSearchChange('')}>
                <Icon name="X" size={12} />
              </button>
            </div>
          )}

          {statusFilter !== 'all' && (
            <div className="flex items-center space-x-1 px-2 py-1 bg-accent/10 text-accent rounded-full text-xs">
              <Icon name="Filter" size={12} />
              <span>{statusOptions?.find(opt => opt?.value === statusFilter)?.label}</span>
              <button onClick={() => onStatusFilterChange('all')}>
                <Icon name="X" size={12} />
              </button>
            </div>
          )}

          {serviceFilter !== 'all' && (
            <div className="flex items-center space-x-1 px-2 py-1 bg-success/10 text-success rounded-full text-xs">
              <Icon name="Scissors" size={12} />
              <span>{serviceOptions?.find(opt => opt?.value === serviceFilter)?.label}</span>
              <button onClick={() => onServiceFilterChange('all')}>
                <Icon name="X" size={12} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientFilters;
